/**
 * Template validation utilities for nested template functionality
 */

/**
 * Detects if adding a template reference would create a circular dependency
 * @param {string} templateId - The template being edited
 * @param {string} referencedTemplateId - The template to be referenced
 * @param {Array} allTemplates - All available templates
 * @returns {boolean} - True if circular reference detected, false otherwise
 */
export function hasCircularReference(templateId, referencedTemplateId, allTemplates) {
  if (!templateId || !referencedTemplateId) return false;
  
  // If referencing self, that's circular
  if (templateId === referencedTemplateId) {
    return true;
  }

  // Build a dependency graph
  const graph = buildDependencyGraph(allTemplates);
  
  // Check if referencedTemplateId eventually leads back to templateId
  return detectCycle(referencedTemplateId, templateId, graph, new Set());
}

/**
 * Builds a dependency graph from templates
 * @param {Array} templates - All templates
 * @returns {Map} - Map of templateId -> array of referenced template IDs
 */
function buildDependencyGraph(templates) {
  const graph = new Map();
  
  if (!Array.isArray(templates)) return graph;
  
  templates.forEach(template => {
    const deps = [];
    
    if (Array.isArray(template.definition)) {
      template.definition.forEach(field => {
        if (field.type === 'template-reference' && field.templateId) {
          deps.push(field.templateId);
        }
      });
    }
    
    // Use both id and templateId as keys (id is slug, templateId is UUID)
    if (template.id) graph.set(template.id, deps);
    if (template.templateId) graph.set(template.templateId, deps);
  });
  
  return graph;
}

/**
 * Detects if there's a path from start to target in the dependency graph
 * @param {string} current - Current node in traversal
 * @param {string} target - Target node we're looking for
 * @param {Map} graph - Dependency graph
 * @param {Set} visited - Set of visited nodes to prevent infinite loops
 * @returns {boolean} - True if path exists (circular reference found)
 */
function detectCycle(current, target, graph, visited) {
  if (current === target) return true;
  if (visited.has(current)) return false; // Already checked this path
  
  visited.add(current);
  
  const dependencies = graph.get(current) || [];
  
  for (const dep of dependencies) {
    if (detectCycle(dep, target, graph, visited)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Validates that all required child templates are completed
 * @param {Array} childTemplateRefs - Array of child template references
 * @returns {Object} - { valid: boolean, incomplete: Array }
 */
export function validateRequiredChildren(childTemplateRefs) {
  if (!Array.isArray(childTemplateRefs)) {
    return { valid: true, incomplete: [] };
  }
  
  const required = childTemplateRefs.filter(ref => ref.required);
  const incomplete = required.filter(ref => ref.status !== 'completed');
  
  return {
    valid: incomplete.length === 0,
    incomplete
  };
}

/**
 * Calculates completion percentage for child templates
 * @param {Array} childTemplateRefs - Array of child template references
 * @returns {Object} - { completed: number, total: number, percentage: number }
 */
export function calculateChildCompletion(childTemplateRefs) {
  if (!Array.isArray(childTemplateRefs) || childTemplateRefs.length === 0) {
    return { completed: 0, total: 0, percentage: 0 };
  }
  
  const total = childTemplateRefs.length;
  const completed = childTemplateRefs.filter(ref => ref.status === 'completed').length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  return { completed, total, percentage };
}

/**
 * Gets template name by ID from templates array
 * @param {string} templateId - Template ID (slug or UUID)
 * @param {Array} templates - Array of templates
 * @returns {string} - Template name or 'Unknown Template'
 */
export function getTemplateName(templateId, templates) {
  if (!templateId || !Array.isArray(templates)) return 'Unknown Template';
  
  const template = templates.find(t => 
    t.id === templateId || t.templateId === templateId
  );
  
  return template ? template.name : 'Unknown Template';
}
